import './readerSettings.css'
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { SET_CHINESE_WORD_FONT_SIZE, SET_PINYIN_SETTINGS } from '../../redux/types'
import { savePinyinSettings } from '../../redux/actions/storyAction'
import { FONT_SIZE_LIST } from 'components/account/const'
import { SettingIcon } from 'containers/icons/icons'
import { pinyinSettingOptions } from 'utils/constant'
import { ReaderTextSize, writeTextSize } from 'utils/readerPreferences'
import { PinyinSettings, PinyinVisibility } from 'types/global'

const SHEET_QUERY = '(max-width: 767px)'
const DEFAULT_PINYIN: PinyinSettings = { new_word: 'always', recently_learned_word: 'on_hover', known_word: 'never' }
// Older stored settings say 'hover' for what the server now calls 'on_hover'.
const normalize = (value?: string): PinyinVisibility | undefined => (value === 'hover' ? 'on_hover' : (value as PinyinVisibility))

const TEXT_SIZE_NAMES = [
  'reader:settings.sizes.small',
  'reader:settings.sizes.medium',
  'reader:settings.sizes.large',
  'reader:settings.sizes.largest',
]
const PINYIN_ROWS: { key: keyof PinyinSettings; label: string }[] = [
  { key: 'new_word', label: 'reader:settings.pinyinRows.newWords' },
  { key: 'recently_learned_word', label: 'reader:settings.pinyinRows.recentlyLearned' },
  { key: 'known_word', label: 'reader:settings.pinyinRows.knownWords' },
]

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches ?? false)
  useEffect(() => {
    const list = window.matchMedia?.(query)
    if (!list) return
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener?.('change', onChange)
    return () => list.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}

/**
 * Applies pinyin changes to the story at once and saves them in the
 * background, one request at a time so the server always ends on the last
 * choice. If a save fails with nothing newer queued, the reader goes back to
 * what the server last accepted.
 */
const usePinyinSaver = (current: PinyinSettings) => {
  const dispatch = AppUseDispatch()
  const [failed, setFailed] = useState(false)
  const saving = useRef(false)
  const queued = useRef<PinyinSettings | null>(null)
  const confirmed = useRef(current)

  // Settings loaded from the server count as accepted while nothing is pending.
  useEffect(() => {
    if (!saving.current && !queued.current) confirmed.current = current
  }, [current])

  const send = useCallback(
    async (settings: PinyinSettings) => {
      saving.current = true
      try {
        await savePinyinSettings(settings)
        confirmed.current = settings
        setFailed(false)
      } catch {
        if (!queued.current) {
          dispatch({ type: SET_PINYIN_SETTINGS, payload: confirmed.current })
          setFailed(true)
        }
      }
      saving.current = false
      const next = queued.current
      queued.current = null
      if (next) send(next)
    },
    [dispatch],
  )

  const change = (settings: PinyinSettings) => {
    setFailed(false)
    dispatch({ type: SET_PINYIN_SETTINGS, payload: settings })
    if (saving.current) queued.current = settings
    else send(settings)
  }

  return { change, failed }
}

interface ReaderSettingsProps {
  showEnglish: boolean
  onToggleEnglish: () => void
  /** The story's translation is in the learner's language: the switch says "Translation". */
  translationLocalized?: boolean
}

const ReaderSettings: React.FC<ReaderSettingsProps> = ({ showEnglish, onToggleEnglish, translationLocalized = false }) => {
  const { t } = useTranslation('reader')
  const dispatch = AppUseDispatch()
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }) => dashboard)
  const storedPinyin = AppUseSelector(({ story }) => story.pinyin_settings)
  const pinyin: PinyinSettings = useMemo(
    () => ({
      new_word: normalize(storedPinyin?.new_word) || DEFAULT_PINYIN.new_word,
      recently_learned_word: normalize(storedPinyin?.recently_learned_word) || DEFAULT_PINYIN.recently_learned_word,
      known_word: normalize(storedPinyin?.known_word) || DEFAULT_PINYIN.known_word,
    }),
    [storedPinyin],
  )
  const { change: changePinyin, failed: pinyinFailed } = usePinyinSaver(pinyin)

  const [open, setOpen] = useState(false)
  const isSheet = useMediaQuery(SHEET_QUERY)
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const panelId = `${id}-panel`
  const titleId = `${id}-title`
  // On desktop the popover waits for its position before it is drawn.
  const visible = open && (isSheet || anchor !== null)

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false)
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true })
  }, [])

  // The popover hangs under the button; it is portalled (the reader scrolls
  // inside its own container on desktop), so it follows the button by hand.
  useLayoutEffect(() => {
    if (!open || isSheet) return
    let frame = 0
    const place = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchor({ top: rect.bottom + 10, right: Math.max(16, window.innerWidth - rect.right) })
      })
    }
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setAnchor({ top: rect.bottom + 10, right: Math.max(16, window.innerWidth - rect.right) })
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, isSheet])

  useEffect(() => {
    if (!visible) return
    panelRef.current?.focus({ preventScroll: true })
    const isInside = (target: EventTarget | null) =>
      target instanceof Node && (panelRef.current?.contains(target) || triggerRef.current?.contains(target))
    // A click outside the popover closes it and still reaches what was
    // clicked. The phone sheet closes from its backdrop's click instead, so
    // the tap can't land on a word underneath once the sheet is gone.
    const onMouseDown = (event: MouseEvent) => {
      if (!isSheet && !isInside(event.target)) close(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close(true)
      }
    }
    // Tabbing out of the popover closes it; clicks on nothing focusable don't.
    const onFocusIn = (event: FocusEvent) => {
      if (event.target !== document.body && !isInside(event.target)) close(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [visible, isSheet, close])

  const changeTextSize = (size: ReaderTextSize) => {
    dispatch({ type: SET_CHINESE_WORD_FONT_SIZE, payload: size })
    writeTextSize(size)
  }

  const panel = (
    <div
      ref={panelRef}
      id={panelId}
      role='dialog'
      aria-labelledby={titleId}
      aria-modal={isSheet || undefined}
      tabIndex={-1}
      className={`rs-panel ${isSheet ? 'rs-panel--sheet' : 'rs-panel--popover'}`}
      style={
        !isSheet && anchor
          ? { top: anchor.top, right: anchor.right, maxHeight: `calc(100vh - ${anchor.top + 16}px)` }
          : undefined
      }
    >
      {isSheet && <div className='rs-grabber' aria-hidden='true' />}
      <div className='rs-header'>
        <h2 id={titleId} className='rs-title'>
          {t('settings.title')}
        </h2>
        <button type='button' className='rs-close' aria-label={t('settings.close')} onClick={() => close(true)}>
          <svg width='14' height='14' viewBox='0 0 14 14' aria-hidden='true'>
            <path d='M1 1l12 12M13 1L1 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
          </svg>
        </button>
      </div>

      <section className='rs-section'>
        <h3 className='rs-label' id={`${id}-size`}>
          {t('settings.textSize')}
        </h3>
        <div className='rs-segmented rs-segmented--size' role='radiogroup' aria-labelledby={`${id}-size`}>
          {FONT_SIZE_LIST.map((size, index) => (
            <label key={size.label} className='rs-option'>
              <input
                type='radio'
                className='rs-option-input'
                name={`${id}-size`}
                checked={chineseWordFontSize?.storyContent === size.storyContent}
                onChange={() => changeTextSize(size)}
                aria-label={t(TEXT_SIZE_NAMES[index])}
              />
              <span className='rs-option-face' style={{ fontSize: 14 + index * 3 }} aria-hidden='true'>
                {t('settings.sizeSample')}
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className='rs-section'>
        <button type='button' role='switch' aria-checked={showEnglish} className='rs-switch-row' onClick={onToggleEnglish}>
          <span className='rs-label'>
            {translationLocalized ? t('settings.translation') : t('settings.englishTranslation')}
          </span>
          <span className='rs-switch' aria-hidden='true' />
        </button>
      </section>

      <section className='rs-section'>
        <h3 className='rs-label'>{t('settings.pinyin')}</h3>
        <div className='rs-pinyin-rows'>
          {PINYIN_ROWS.map((row) => (
            <div key={row.key} className='rs-pinyin-row'>
              <span className='rs-row-label' id={`${id}-${row.key}`}>
                {t(row.label)}
              </span>
              <div className='rs-segmented' role='radiogroup' aria-labelledby={`${id}-${row.key}`}>
                {pinyinSettingOptions.map((option) => (
                  <label key={option.value} className='rs-option'>
                    <input
                      type='radio'
                      className='rs-option-input'
                      name={`${id}-${row.key}`}
                      checked={pinyin[row.key] === option.value}
                      onChange={() => changePinyin({ ...pinyin, [row.key]: option.value })}
                    />
                    <span className='rs-option-face'>{option.labelKey ? t(option.labelKey) : option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {pinyinFailed && (
          <p className='rs-error' role='alert'>
            {t('settings.pinyinSaveError')}
          </p>
        )}
      </section>
    </div>
  )

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className='action_btn_text rs-trigger d-flex align-items-center gap-2 p-0'
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <SettingIcon color='currentColor' />
        {t('settings.trigger')}
      </button>
      {visible &&
        createPortal(
          <>
            {isSheet && <div className='rs-backdrop' aria-hidden='true' onClick={() => close(false)} />}
            {panel}
          </>,
          document.body,
        )}
    </>
  )
}

export default ReaderSettings
